import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../../../lib/api';
import {
  ParticipantColor,
  getParticipantColorByIndex,
  getParticipantColorByName,
} from '../utils/participantColors';

export interface PartnerPref {
  nickname?: string | null;
  avatarColor?: string | null;
  customAvatarUrl?: string | null;
}

interface PartnerPreferencesCtx {
  preferences: Record<string, PartnerPref>;
  getDisplayName: (userId: string, fallback: string) => string;
  getColorScheme: (userId: string, fallbackIndex: number) => ParticipantColor;
  getAvatarUrl: (userId: string, partnerOwnUrl?: string | null) => string | null;
  updatePreference: (partnerUserId: string, data: Partial<PartnerPref>) => Promise<void>;
  uploadAvatar: (partnerUserId: string, file: File) => Promise<string>;
}

const PartnerPreferencesContext = createContext<PartnerPreferencesCtx | null>(null);

export function PartnerPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<Record<string, PartnerPref>>({});

  useEffect(() => {
    const load = () => {
      api.getPartnerPreferences().then((res) => {
        if (res.success && res.data) setPreferences(res.data);
      });
    };

    load();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const getDisplayName = useCallback(
    (userId: string, fallback: string) => preferences[userId]?.nickname || fallback,
    [preferences],
  );

  const getColorScheme = useCallback(
    (userId: string, fallbackIndex: number): ParticipantColor => {
      const color = preferences[userId]?.avatarColor;
      return color ? getParticipantColorByName(color) : getParticipantColorByIndex(fallbackIndex);
    },
    [preferences],
  );

  const getAvatarUrl = useCallback(
    (userId: string, partnerOwnUrl?: string | null): string | null =>
      preferences[userId]?.customAvatarUrl ?? partnerOwnUrl ?? null,
    [preferences],
  );

  const updatePreference = useCallback(async (partnerUserId: string, data: Partial<PartnerPref>) => {
    const res = await api.upsertPartnerPreference(partnerUserId, data);
    if (res.success && res.data) {
      setPreferences((prev) => ({
        ...prev,
        [partnerUserId]: { ...prev[partnerUserId], ...res.data },
      }));
    }
  }, []);

  const uploadAvatar = useCallback(async (partnerUserId: string, file: File): Promise<string> => {
    const res = await api.uploadPartnerAvatar(partnerUserId, file);
    if (!res.success || !res.data) {
      const errMsg = res.error
        ? (typeof res.error === 'string' ? res.error : res.error.message)
        : 'Upload failed (no response data)';
      throw new Error(errMsg);
    }
    const { avatarUrl } = res.data;
    return avatarUrl;
  }, []);

  const value = useMemo(
    () => ({ preferences, getDisplayName, getColorScheme, getAvatarUrl, updatePreference, uploadAvatar }),
    [preferences, getDisplayName, getColorScheme, getAvatarUrl, updatePreference, uploadAvatar],
  );

  return (
    <PartnerPreferencesContext.Provider value={value}>
      {children}
    </PartnerPreferencesContext.Provider>
  );
}

const FALLBACK: PartnerPreferencesCtx = {
  preferences: {},
  getDisplayName: (_uid: string, fallback: string) => fallback,
  getColorScheme: (_uid: string, fallbackIndex: number) => getParticipantColorByIndex(fallbackIndex),
  getAvatarUrl: (_uid: string, partnerOwnUrl?: string | null) => partnerOwnUrl || null,
  updatePreference: async () => {},
  uploadAvatar: async () => '',
};

export function usePartnerPreferences(): PartnerPreferencesCtx {
  const ctx = useContext(PartnerPreferencesContext);
  return ctx || FALLBACK;
}
